import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Package, ShoppingCart, MessageSquare, Video, BarChart3,
  Plus, Trash2, Edit, Upload, Check, X, LogOut, Eye, Star, Film, Settings, Send, Mail
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../lib/api';
import ContentManager from '../components/ContentManager';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = window.location;
  const [activeTab, setActiveTab] = useState('overview');
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  // Check auth on mount - support both Google OAuth and token-based auth
  useEffect(() => {
    // Skip if returning from OAuth callback (AuthCallback handles this)
    if (location.hash?.includes('session_id=')) return;

    const checkAuth = async () => {
      // Check Google OAuth session first
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setUser(data);
          setAuthChecked(true);
          return;
        }
      } catch {}

      // Fallback to token-based admin auth
      if (localStorage.getItem('admin_token')) {
        const adminUser = JSON.parse(localStorage.getItem('admin_user') || '{}');
        setUser(adminUser);
        setAuthChecked(true);
        return;
      }

      navigate('/admin/login');
    };
    checkAuth();
  }, [navigate, location.hash]);

  // Handle user passed from AuthCallback
  useEffect(() => {
    if (window.history.state?.usr?.user) {
      setUser(window.history.state.usr.user);
      setAuthChecked(true);
    }
  }, []);

  const handleLogout = async () => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch {}
    api.adminLogout().catch(() => {});
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  if (!authChecked && !user) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b px-6 py-4 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <img src="/images/coras-logo.png" alt="Logo" className="h-12" />
          <div>
            <h1 className="text-lg font-semibold" style={{ fontFamily: "'Playfair Display', 'Georgia', serif" }}>Admin Dashboard</h1>
            <p className="text-xs text-gray-500">{user?.email || ''}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {user?.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />}
          <Button variant="outline" size="sm" onClick={() => navigate('/')} data-testid="view-site-btn">
            <Eye size={14} className="mr-1" /> View Site
          </Button>
          <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
            <LogOut size={14} className="mr-1" /> Logout
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-7 mb-6">
            <TabsTrigger value="overview" data-testid="tab-overview"><BarChart3 size={14} className="mr-1" /> Overview</TabsTrigger>
            <TabsTrigger value="content" data-testid="tab-content"><Settings size={14} className="mr-1" /> Content</TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products"><Package size={14} className="mr-1" /> Products</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders"><ShoppingCart size={14} className="mr-1" /> Orders</TabsTrigger>
            <TabsTrigger value="testimonials" data-testid="tab-testimonials"><MessageSquare size={14} className="mr-1" /> Reviews</TabsTrigger>
            <TabsTrigger value="videos" data-testid="tab-videos"><Video size={14} className="mr-1" /> Videos</TabsTrigger>
            <TabsTrigger value="email" data-testid="tab-email"><Mail size={14} className="mr-1" /> Email</TabsTrigger>
          </TabsList>

          <TabsContent value="overview"><OverviewTab /></TabsContent>
          <TabsContent value="content"><ContentManager /></TabsContent>
          <TabsContent value="products"><ProductsTab /></TabsContent>
          <TabsContent value="orders"><OrdersTab /></TabsContent>
          <TabsContent value="testimonials"><TestimonialsTab /></TabsContent>
          <TabsContent value="videos"><VideosTab /></TabsContent>
          <TabsContent value="email"><EmailTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

/* ========== OVERVIEW TAB ========== */
function OverviewTab() {
  const [stats, setStats] = useState(null);
  const [quizStats, setQuizStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.getSalesAnalytics().then(res => setStats(res.data)).catch(() => {}),
      api.getQuizAnalytics().then(res => setQuizStats(res.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-12 text-gray-500">Loading analytics...</div>;

  const maxBlendCount = quizStats?.blend_breakdown?.[0]?.count || 1;

  return (
    <div className="space-y-6" data-testid="overview-tab">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard title="Total Revenue" value={`$${(stats?.total_revenue || 0).toFixed(2)}`} icon={<BarChart3 />} />
        <StatCard title="Total Orders" value={stats?.total_orders || 0} icon={<ShoppingCart />} />
        <StatCard title="Pending Orders" value={stats?.pending_orders || 0} icon={<Package />} />
        <StatCard title="Shipped Orders" value={stats?.shipped_orders || 0} icon={<Check />} />
      </div>

      {/* Quiz Analytics */}
      <Card data-testid="quiz-analytics-card">
        <CardHeader><CardTitle className="text-base">Quiz Analytics</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center gap-6 mb-4">
            <div>
              <p className="text-3xl font-bold" style={{ color: '#4A5D3A' }}>{quizStats?.total_completions || 0}</p>
              <p className="text-sm text-gray-500">Total Quizzes Completed</p>
            </div>
          </div>
          {quizStats?.blend_breakdown?.length > 0 ? (
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-600 mb-2">Most Recommended Blends</p>
              {quizStats.blend_breakdown.map((b, i) => (
                <div key={b.blend} className="flex items-center gap-3" data-testid={`quiz-blend-${b.blend.toLowerCase()}`}>
                  <span className="w-24 text-sm font-medium">{b.blend}</span>
                  <div className="flex-1 h-7 bg-gray-100 rounded overflow-hidden">
                    <div
                      className="h-full rounded flex items-center px-2"
                      style={{
                        width: `${(b.count / maxBlendCount) * 100}%`,
                        background: i === 0 ? '#4A5D3A' : i === 1 ? '#6B7F5A' : i === 2 ? '#6b2737' : '#8B9D7A',
                        minWidth: '40px'
                      }}
                    >
                      <span className="text-white text-xs font-semibold">{b.count} ({b.percentage}%)</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-sm">No quiz completions yet</p>
          )}
        </CardContent>
      </Card>

      {stats?.top_selling_products?.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Top Selling Products</CardTitle></CardHeader>
          <CardContent>
            {stats.top_selling_products.map((p, i) => (
              <div key={i} className="flex justify-between py-2 border-b last:border-0">
                <span>{p.product_name}</span>
                <span className="text-gray-500">{p.quantity} sold - ${p.revenue.toFixed(2)}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StatCard({ title, value, icon }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
          </div>
          <div className="text-[#4A5D3A]">{icon}</div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ========== PRODUCTS TAB ========== */
function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', price: '', size: '2 oz', stock: '', ingredients: '', benefits: '' });

  const load = useCallback(() => {
    api.getAdminProducts()
      .then(res => setProducts(res.data))
      .catch(() => toast.error('Failed to load products'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    const data = {
      ...form,
      price: parseFloat(form.price),
      stock: parseInt(form.stock) || 0,
      ingredients: form.ingredients.split('\n').filter(Boolean),
      benefits: form.benefits.split('\n').filter(Boolean),
    };
    try {
      if (editing) {
        await api.updateProduct(editing, data);
        toast.success('Product updated');
      } else {
        await api.createProduct(data);
        toast.success('Product created');
      }
      setShowForm(false);
      setEditing(null);
      setForm({ name: '', description: '', price: '', size: '2 oz', stock: '', ingredients: '', benefits: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
  };

  const handleEdit = (p) => {
    setEditing(p.id);
    setForm({
      name: p.name, description: p.description, price: String(p.price),
      size: p.size, stock: String(p.stock),
      ingredients: (p.ingredients || []).join('\n'),
      benefits: (p.benefits || []).join('\n'),
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await api.deleteProduct(id);
      toast.success('Product deleted');
      load();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const handleImageUpload = async (productId, e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      await api.uploadProductImage(productId, file);
      toast.success('Image uploaded');
      load();
    } catch (e) {
      toast.error('Upload failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="products-tab">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Products</h2>
        <Button onClick={() => { setEditing(null); setForm({ name: '', description: '', price: '', size: '2 oz', stock: '', ingredients: '', benefits: '' }); setShowForm(true); }} data-testid="add-product-btn">
          <Plus size={14} className="mr-1" /> Add Product
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="product-name-input" /></div>
              <div><Label>Size</Label><Input value={form.size} onChange={e => setForm({ ...form, size: e.target.value })} /></div>
              <div><Label>Price ($)</Label><Input type="number" step="0.01" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} data-testid="product-price-input" /></div>
              <div><Label>Stock</Label><Input type="number" value={form.stock} onChange={e => setForm({ ...form, stock: e.target.value })} /></div>
            </div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="product-desc-input" /></div>
            <div><Label>Ingredients (one per line)</Label><Textarea value={form.ingredients} onChange={e => setForm({ ...form, ingredients: e.target.value })} rows={4} /></div>
            <div><Label>Benefits (one per line)</Label><Textarea value={form.benefits} onChange={e => setForm({ ...form, benefits: e.target.value })} rows={4} /></div>
            <div className="flex gap-2">
              <Button onClick={handleSave} style={{ backgroundColor: '#4A5D3A' }} data-testid="save-product-btn">{editing ? 'Update' : 'Create'} Product</Button>
              <Button variant="outline" onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? <p className="text-gray-500 py-8 text-center">Loading...</p> : (
        <div className="space-y-3">
          {products.map(p => (
            <Card key={p.id}>
              <CardContent className="pt-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-16 h-16 object-cover rounded" />}
                  <div>
                    <h3 className="font-semibold">{p.name}</h3>
                    <p className="text-sm text-gray-500">${p.price} | Stock: {p.stock} | {p.size}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(p.id, e)} />
                    <Button variant="outline" size="sm" asChild><span><Upload size={14} className="mr-1" /> Image</span></Button>
                  </label>
                  <Button variant="outline" size="sm" onClick={() => handleEdit(p)} data-testid={`edit-product-${p.id}`}><Edit size={14} /></Button>
                  <Button variant="destructive" size="sm" onClick={() => handleDelete(p.id)} data-testid={`delete-product-${p.id}`}><Trash2 size={14} /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {products.length === 0 && <p className="text-gray-400 text-center py-8">No products yet</p>}
        </div>
      )}
    </div>
  );
}

/* ========== ORDERS TAB ========== */
function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    api.getAdminOrders()
      .then(res => setOrders(res.data))
      .catch(() => toast.error('Failed to load orders'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (orderId, status) => {
    try {
      await api.updateOrderStatus(orderId, status);
      toast.success('Order status updated');
      load();
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    paid: 'bg-green-100 text-green-800',
    shipped: 'bg-blue-100 text-blue-800',
    delivered: 'bg-emerald-100 text-emerald-800',
    cancelled: 'bg-red-100 text-red-800',
  };

  return (
    <div className="space-y-4" data-testid="orders-tab">
      <h2 className="text-xl font-semibold">Orders</h2>
      {loading ? <p className="text-gray-500 py-8 text-center">Loading...</p> : orders.length === 0 ? (
        <p className="text-gray-400 text-center py-8">No orders yet. Orders placed through Shopify will appear here once integrated.</p>
      ) : (
        <div className="rounded-lg border bg-white overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order #</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map(order => (
                <TableRow key={order.id}>
                  <TableCell className="font-mono text-sm">{order.order_number}</TableCell>
                  <TableCell>
                    <div>{order.customer_name}</div>
                    <div className="text-xs text-gray-500">{order.customer_email}</div>
                  </TableCell>
                  <TableCell>{order.items?.map(i => `${i.product_name} x${i.quantity}`).join(', ')}</TableCell>
                  <TableCell className="font-semibold">${order.total_amount?.toFixed(2)}</TableCell>
                  <TableCell><Badge className={statusColors[order.payment_status] || ''}>{order.payment_status}</Badge></TableCell>
                  <TableCell><Badge className={statusColors[order.status] || ''}>{order.status}</Badge></TableCell>
                  <TableCell>
                    <Select onValueChange={(val) => handleStatusChange(order.id, val)}>
                      <SelectTrigger className="w-32 h-8 text-xs"><SelectValue placeholder="Update" /></SelectTrigger>
                      <SelectContent>
                        {['pending', 'paid', 'shipped', 'delivered', 'cancelled'].map(s => (
                          <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

/* ========== TESTIMONIALS TAB ========== */
function TestimonialsTab() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    api.getAdminTestimonials()
      .then(res => setTestimonials(res.data))
      .catch(() => toast.error('Failed to load testimonials'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (id, approved) => {
    try {
      await api.updateTestimonial(id, { is_approved: approved });
      toast.success(approved ? 'Testimonial approved' : 'Testimonial hidden');
      load();
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try {
      await api.deleteTestimonial(id);
      toast.success('Testimonial deleted');
      load();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="space-y-4" data-testid="testimonials-tab">
      <h2 className="text-xl font-semibold">Customer Reviews & Stories</h2>
      {loading ? <p className="text-gray-500 py-8 text-center">Loading...</p> : (
        <div className="space-y-3">
          {testimonials.map(t => (
            <Card key={t.id}>
              <CardContent className="pt-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold">{t.customer_name}</span>
                      <Badge variant={t.is_approved ? 'default' : 'secondary'}>
                        {t.is_approved ? 'Approved' : 'Pending'}
                      </Badge>
                      <span className="text-sm text-gray-500">{t.hair_type}</span>
                    </div>
                    <div className="flex gap-0.5 text-[#4A5D3A] mb-2">
                      {[...Array(t.rating || 5)].map((_, i) => <Star key={i} size={14} fill="currentColor" />)}
                    </div>
                    <p className="text-sm text-gray-600 mb-1">"{t.testimonial_text}"</p>
                    <p className="text-xs text-[#4A5D3A] font-medium">{t.results_summary}</p>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button variant="outline" size="sm" onClick={() => handleApprove(t.id, !t.is_approved)} data-testid={`toggle-approve-${t.id}`}>
                      {t.is_approved ? <X size={14} /> : <Check size={14} />}
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => handleDelete(t.id)} data-testid={`delete-testimonial-${t.id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {testimonials.length === 0 && <p className="text-gray-400 text-center py-8">No testimonials yet</p>}
        </div>
      )}
    </div>
  );
}

/* ========== VIDEOS TAB ========== */
function VideosTab() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    api.getAdminVideos()
      .then(res => setVideos(res.data))
      .catch(() => toast.error('Failed to load videos'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      await api.uploadVideo(file);
      toast.success('Video uploaded successfully');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleActivate = async (id) => {
    try {
      await api.activateVideo(id);
      toast.success('Video activated');
      load();
    } catch (e) {
      toast.error('Failed to activate');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this video?')) return;
    try {
      await api.deleteVideo(id);
      toast.success('Video deleted');
      load();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="space-y-4" data-testid="videos-tab">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Advertisement Videos</h2>
        <label className="cursor-pointer">
          <input type="file" accept="video/mp4,video/webm,video/ogg" className="hidden" onChange={handleUpload} disabled={uploading} />
          <Button asChild disabled={uploading} data-testid="upload-video-btn">
            <span><Upload size={14} className="mr-1" /> {uploading ? 'Uploading...' : 'Upload Video'}</span>
          </Button>
        </label>
      </div>

      {loading ? <p className="text-gray-500 py-8 text-center">Loading...</p> : (
        <div className="space-y-3">
          {videos.map(v => (
            <Card key={v.id}>
              <CardContent className="pt-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Film size={24} className="text-gray-400" />
                  <div>
                    <p className="font-medium">{v.original_name || v.filename}</p>
                    <p className="text-xs text-gray-500">Uploaded: {new Date(v.uploaded_at).toLocaleDateString()}</p>
                  </div>
                  {v.is_active && <Badge className="bg-green-100 text-green-800">Active</Badge>}
                </div>
                <div className="flex gap-2">
                  {!v.is_active && (
                    <Button variant="outline" size="sm" onClick={() => handleActivate(v.id)} data-testid={`activate-video-${v.id}`}>
                      <Check size={14} className="mr-1" /> Set Active
                    </Button>
                  )}
                  <Button variant="destructive" size="sm" onClick={() => handleDelete(v.id)} data-testid={`delete-video-${v.id}`}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {videos.length === 0 && <p className="text-gray-400 text-center py-8">No videos uploaded yet. Upload an advertisement video to display on your site.</p>}
        </div>
      )}
    </div>
  );
}


/* ========== EMAIL TAB ========== */
function EmailTab() {
  const [subscribers, setSubscribers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [referrals, setReferrals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [result, setResult] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [newTemplate, setNewTemplate] = useState(null);

  const load = useCallback(() => {
    Promise.all([
      api.getNewsletterSubscribers().then(res => setSubscribers(res.data)).catch(() => {}),
      api.getEmailTemplates().then(res => setTemplates(res.data)).catch(() => {}),
      api.getReferralAnalytics().then(res => setReferrals(res.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSendTemplate = async () => {
    if (!selectedTemplate) return toast.error('Please select a template first');
    const tpl = templates.find(t => t.id === selectedTemplate);
    if (!window.confirm(`Send "${tpl?.name}" to ${subscribers.length} subscribers?`)) return;
    setSending(true);
    try {
      const res = await api.sendNewsletterTemplate(selectedTemplate);
      setResult(res.data);
      toast.success(`Email sent to ${res.data.sent} subscribers!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const handleSaveTemplate = async (tpl) => {
    try {
      if (tpl.id) {
        await api.updateEmailTemplate(tpl.id, { name: tpl.name, subject: tpl.subject, body: tpl.body });
        toast.success('Template updated');
      } else {
        await api.createEmailTemplate({ name: tpl.name, subject: tpl.subject, body: tpl.body });
        toast.success('Template created');
      }
      setEditingTemplate(null);
      setNewTemplate(null);
      load();
    } catch (e) {
      toast.error('Failed to save template');
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await api.deleteEmailTemplate(id);
      toast.success('Template deleted');
      load();
    } catch (e) {
      toast.error('Failed to delete template');
    }
  };

  if (loading) return <div className="text-center py-8 text-gray-500">Loading...</div>;

  return (
    <div className="space-y-4" data-testid="email-tab">
      <h2 className="text-xl font-semibold">Newsletter & Email</h2>

      {/* Send Email with Template */}
      <Card>
        <CardContent className="pt-6">
          <h3 className="font-semibold text-base mb-3">Send Email to Subscribers</h3>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="text-sm text-gray-500 block mb-1">Choose a template</label>
              <select
                className="w-full border rounded px-3 py-2 text-sm"
                value={selectedTemplate}
                onChange={e => setSelectedTemplate(e.target.value)}
                data-testid="template-select"
              >
                <option value="">Select a template...</option>
                {templates.map(t => (
                  <option key={t.id} value={t.id}>{t.name} — {t.subject}</option>
                ))}
              </select>
            </div>
            <Button
              onClick={handleSendTemplate}
              disabled={sending || !selectedTemplate || subscribers.length === 0}
              style={{ backgroundColor: '#4A5D3A' }}
              data-testid="send-template-btn"
            >
              <Send size={14} className="mr-1" />
              {sending ? 'Sending...' : `Send to ${subscribers.length}`}
            </Button>
          </div>
          {result && (
            <div className="mt-3 p-3 bg-green-50 rounded text-sm text-green-800" data-testid="send-result">
              Sent: {result.sent} | Failed: {result.failed}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Templates */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Email Templates ({templates.length})</CardTitle>
            <Button size="sm" variant="outline" onClick={() => setNewTemplate({ name: '', subject: '', body: '' })} data-testid="new-template-btn">
              <Plus size={14} className="mr-1" /> New Template
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* New template form */}
          {newTemplate && (
            <div className="border rounded p-4 mb-4 bg-gray-50 space-y-3" data-testid="new-template-form">
              <input className="w-full border rounded px-3 py-2 text-sm" placeholder="Template name" value={newTemplate.name} onChange={e => setNewTemplate({ ...newTemplate, name: e.target.value })} />
              <input className="w-full border rounded px-3 py-2 text-sm" placeholder="Subject line" value={newTemplate.subject} onChange={e => setNewTemplate({ ...newTemplate, subject: e.target.value })} />
              <textarea className="w-full border rounded px-3 py-2 text-sm" rows={4} placeholder="Email body (HTML supported, use {{shop_url}} for your shop link)" value={newTemplate.body} onChange={e => setNewTemplate({ ...newTemplate, body: e.target.value })} />
              <div className="flex gap-2">
                <Button size="sm" style={{ backgroundColor: '#4A5D3A' }} onClick={() => handleSaveTemplate(newTemplate)}>Save</Button>
                <Button size="sm" variant="outline" onClick={() => setNewTemplate(null)}>Cancel</Button>
              </div>
            </div>
          )}
          {templates.map(t => (
            <div key={t.id} className="border-b py-3 last:border-0">
              {editingTemplate?.id === t.id ? (
                <div className="space-y-2" data-testid={`edit-template-${t.id}`}>
                  <input className="w-full border rounded px-3 py-2 text-sm" value={editingTemplate.name} onChange={e => setEditingTemplate({ ...editingTemplate, name: e.target.value })} />
                  <input className="w-full border rounded px-3 py-2 text-sm" value={editingTemplate.subject} onChange={e => setEditingTemplate({ ...editingTemplate, subject: e.target.value })} />
                  <textarea className="w-full border rounded px-3 py-2 text-sm" rows={4} value={editingTemplate.body} onChange={e => setEditingTemplate({ ...editingTemplate, body: e.target.value })} />
                  <div className="flex gap-2">
                    <Button size="sm" style={{ backgroundColor: '#4A5D3A' }} onClick={() => handleSaveTemplate(editingTemplate)}>Save</Button>
                    <Button size="sm" variant="outline" onClick={() => setEditingTemplate(null)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between" data-testid={`template-row-${t.id}`}>
                  <div>
                    <p className="font-medium text-sm">{t.name}</p>
                    <p className="text-xs text-gray-500">Subject: {t.subject}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditingTemplate({ ...t })} data-testid={`edit-template-btn-${t.id}`}>
                      <Edit size={12} className="mr-1" /> Edit
                    </Button>
                    {!t.is_default && (
                      <Button size="sm" variant="destructive" onClick={() => handleDeleteTemplate(t.id)} data-testid={`delete-template-btn-${t.id}`}>
                        <Trash2 size={12} />
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Referral Leaderboard */}
      <Card data-testid="referral-card">
        <CardHeader>
          <CardTitle className="text-base">Referral Program</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500 mb-3">Total referrals: <span className="font-bold" style={{ color: '#4A5D3A' }}>{referrals?.total_referrals || 0}</span></p>
          {referrals?.leaderboard?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Subscriber</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Referrals</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {referrals.leaderboard.map((r, i) => (
                  <TableRow key={r.referral_code}>
                    <TableCell className="font-bold">{i + 1}</TableCell>
                    <TableCell>{r.email}</TableCell>
                    <TableCell className="font-mono text-xs">{r.referral_code}</TableCell>
                    <TableCell className="font-semibold" style={{ color: '#4A5D3A' }}>{r.referrals}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-gray-400 text-sm">No referrals yet. Subscribers get a referral link when they sign up.</p>
          )}
        </CardContent>
      </Card>

      {/* Subscriber List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Subscribers ({subscribers.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {subscribers.length === 0 ? (
            <p className="text-gray-400 text-center py-4">No subscribers yet</p>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Referral Code</TableHead>
                    <TableHead>Referred By</TableHead>
                    <TableHead>Subscribed</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subscribers.map((s, i) => (
                    <TableRow key={i}>
                      <TableCell>{s.email}</TableCell>
                      <TableCell className="font-mono text-xs">{s.referral_code || '—'}</TableCell>
                      <TableCell className="text-xs">{s.referred_by || '—'}</TableCell>
                      <TableCell className="text-gray-500 text-sm">{new Date(s.subscribed_at).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
